import { useEffect, useMemo } from "react";
import { create } from "zustand";

import { WORLD_SESSION_TIMES_MAX } from "@cleo/contracts";

import { readWorldSessionTimes } from "./worldSessionClient";

// W-ea4c5090 (Jason, 19 Oct 2026: "when a session is archived, any alerts
// should be settled or removed from this list"): a World session is archived
// by retiring it in the World, which never archives the Desk's own thread, so
// the room rail (which reads the World) let it go while the agent strip and the
// Inbox's Finished cards kept alerting for weeks. This is the one record of
// which World sessions the World says are retired: the rail writes it the
// moment it retires one (or the World tells it of one), and the strip asks
// each World about the sessions it would show. View only; nothing is written to
// a World or a thread.

const REFRESH_MS = 5 * 60_000;

export const retiredWorldSessionKey = (worldKey: string, sessionId: string): string =>
  `${worldKey}\n${sessionId}`;

interface RetiredWorldSessionsState {
  /** `retiredWorldSessionKey` → whether the World last said it is retired. */
  readonly byKey: ReadonlyMap<string, boolean>;
  note: (worldKey: string, sessionIds: ReadonlyArray<string>, retired: boolean) => void;
}

export const useRetiredWorldSessionsStore = create<RetiredWorldSessionsState>((set) => ({
  byKey: new Map(),
  note: (worldKey, sessionIds, retired) =>
    set((state) => {
      const changed = sessionIds.filter(
        (id) => state.byKey.get(retiredWorldSessionKey(worldKey, id)) !== retired,
      );
      if (changed.length === 0) return state;
      const byKey = new Map(state.byKey);
      for (const id of changed) byKey.set(retiredWorldSessionKey(worldKey, id), retired);
      return { byKey };
    }),
}));

/** Tell every surface that the World retired (or no longer retires) these sessions. */
export const noteWorldSessionsRetired = (
  worldKey: string,
  sessionIds: ReadonlyArray<string>,
  retired = true,
): void => useRetiredWorldSessionsStore.getState().note(worldKey, sessionIds, retired);

/** The keys the World says are retired, for `projectAgentStrip`. */
export function retiredKeySet(byKey: ReadonlyMap<string, boolean>): ReadonlySet<string> {
  const keys = new Set<string>();
  for (const [key, retired] of byKey) if (retired) keys.add(key);
  return keys;
}

export interface RetiredSessionCandidate {
  readonly worldKey: string | null;
  readonly sessionId?: string | null | undefined;
  /** Changes when the session's state does, so a finished turn asks again. */
  readonly status?: string | null | undefined;
}

/**
 * Asks each World whether these sessions are retired: when the set or any
 * session's state changes (Cleo's tidy or retire_session ends her turn), and
 * every REFRESH_MS (another device). A session already known retired is not
 * asked again. A failed read leaves what was known.
 */
export function useWorldRetiredSessions(
  candidates: ReadonlyArray<RetiredSessionCandidate>,
  now: number,
): ReadonlySet<string> {
  const byKey = useRetiredWorldSessionsStore((state) => state.byKey);
  const slot = Math.floor(now / REFRESH_MS);
  const signature = useMemo(
    () =>
      candidates
        .filter((row) => row.worldKey !== null && row.sessionId != null)
        .map((row) => `${row.worldKey}\n${row.sessionId}\n${row.status ?? ""}`)
        .toSorted()
        .join("\n\n"),
    [candidates],
  );
  useEffect(() => {
    if (signature === "") return;
    const known = useRetiredWorldSessionsStore.getState().byKey;
    const byWorld = new Map<string, string[]>();
    for (const row of signature.split("\n\n")) {
      const [worldKey, sessionId] = row.split("\n") as [string, string];
      if (known.get(retiredWorldSessionKey(worldKey, sessionId)) === true) continue;
      const ids = byWorld.get(worldKey);
      if (ids === undefined) byWorld.set(worldKey, [sessionId]);
      else if (!ids.includes(sessionId)) ids.push(sessionId);
    }
    const controller = new AbortController();
    for (const [worldKey, ids] of byWorld)
      for (let at = 0; at < ids.length; at += WORLD_SESSION_TIMES_MAX) {
        const asked = ids.slice(at, at + WORLD_SESSION_TIMES_MAX);
        void readWorldSessionTimes(worldKey, asked, undefined, controller.signal).then(
          (times) => {
            if (times === undefined || controller.signal.aborted) return;
            const retired = asked.filter((id) => times.get(id)?.retired === true);
            const open = asked.filter((id) => times.has(id) && times.get(id)?.retired !== true);
            if (retired.length > 0) noteWorldSessionsRetired(worldKey, retired, true);
            if (open.length > 0) noteWorldSessionsRetired(worldKey, open, false);
          },
          () => undefined,
        );
      }
    return () => controller.abort();
  }, [signature, slot]);
  return useMemo(() => retiredKeySet(byKey), [byKey]);
}
